/* eslint-disable @typescript-eslint/no-explicit-any*/
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import AdminLayout from '@/components/admin/AdminLayout.vue';
import AdminOverview from '@/components/admin/AdminOverviewPage.vue';
import AdminUsersPage from '@/components/admin/AdminUsersPage.vue';
import AdminDomainsPage from '@/components/admin/domain/AdminDomainsPage.vue';

import Home from '@/views/default/Home.vue';
import ContactPage from '@/views/contact/ContactPage.vue';
import LoginPage from '@/views/registration/LoginPage.vue';
import RegisterPage from '@/views/registration/RegisterPage.vue';
import ForgotPasswordPage from '@/views/registration/ForgotPassword.vue';
import ResetPassword from '@/views/registration/ResetPassword.vue';

import ConfiguratorPage from '@/views/ConfiguratorPage.vue';
import CustomizeOverview from '@/components/configurator/CustomizeOverview.vue';

import ProjectPage from '@/views/project/ProjectPage.vue';
import TemplatePage from '@/views/template/TemplatePage.vue';
import TemplateWorkflow from '@/views/template/TemplateWorkflow.vue';
import TemplateUpload from '@/views/template/TemplateUpload.vue';

import OrderPage from '@/views/order/OrderPage.vue';

import OrderWorkflow from '@/views/order/OrderWorkflow.vue';
import OrderUpload from '@/views/order/OrderUpload.vue';

import { useUserStore } from '@/stores/userStore';
import { Role } from '@/types/enum/user';

import { getProductTypeIdsByUrlSuffix } from '@/services/api/modelService';
import * as userService from '@/services/api/userService';
import AdminCreateDomainPage from '@/components/admin/domain/AdminCreateDomainPage.vue';
import AdminEditDomainPage from '@/components/admin/domain/AdminEditDomainPage.vue';


function pickPrimaryDomain(resolved: any, storeDomain: any): { urlSuffix?: string } | null {
  const domain = Array.isArray(resolved) ? resolved[0] : resolved;
  if (domain?.urlSuffix) return domain;
  if (storeDomain?.urlSuffix) return storeDomain;
  return null;
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: Home,
    props: (route) => ({ supplierUrl: route.query.supplier ?? 'aidddo' }),
    beforeEnter: async (to, _from, next) => {
      const userStore = useUserStore();

      const supplierParam =
        to.query.supplier?.toString() ?? localStorage.getItem('supplier') ?? 'aidddo';

      let supplier = supplierParam;

      try {
        if (userStore.user?.email) {
          const resolved = await userService.resolveUserDomains(userStore.user);
          const primary = pickPrimaryDomain(resolved, userStore.domain);
          (userStore as any).setDomain?.(primary ?? null);
          if (primary?.urlSuffix) supplier = primary.urlSuffix;
        } else if (userStore.domain?.urlSuffix) {
          supplier = userStore.domain.urlSuffix;
        }

        const { suffix } = await getProductTypeIdsByUrlSuffix(supplier, 1);
        localStorage.setItem('supplier', suffix);

        if (to.query.supplier?.toString() !== suffix) {
          return next({ path: to.path, query: { ...to.query, supplier: suffix }, replace: true });
        }

        return next();
      } catch (err) {
        console.error('Home supplier normalization failed:', err);
        return next();
      }
    },
  },

  {
    path: '/configurator',
    name: 'configurator',
    component: ConfiguratorPage,
    props: (route) => ({
      productTypeId: parseInt(route.query.productTypeId?.toString() ?? '1', 10),
      supplierUrl: route.query.supplier ?? 'aidddo',
      productId: route.query.productId,
      embossingId: route.query.embossingId,
      wtransferId: route.query.wtransferId,
      colorId: route.query.colorId,
      embossingColorId: route.query.embossingColorId,
    }),
    beforeEnter: async (to, _from, next) => {
      const userStore = useUserStore();

      const supplierParam =
        to.query.supplier?.toString() ?? localStorage.getItem('supplier') ?? 'aidddo';
      const productTypeIdParam = parseInt(to.query.productTypeId?.toString() ?? '1', 10);

      let supplier = supplierParam;

      try {
        if (userStore.user?.email) {
          const resolved = await userService.resolveUserDomains(userStore.user);
          const primary = pickPrimaryDomain(resolved, userStore.domain);
          (userStore as any).setDomain?.(primary ?? null);
          if (primary?.urlSuffix) supplier = primary.urlSuffix;
        } else if (userStore.domain?.urlSuffix) {
          supplier = userStore.domain.urlSuffix;
        }

        const { suffix, pti } = await getProductTypeIdsByUrlSuffix(supplier, productTypeIdParam);
        localStorage.setItem('supplier', suffix);

        const needsSupplierFix = to.query.supplier?.toString() !== suffix;
        const needsPtiFix = parseInt(to.query.productTypeId?.toString() ?? '', 10) !== pti;

        if (needsSupplierFix || needsPtiFix) {
          return next({
            path: to.path,
            query: { ...to.query, supplier: suffix, productTypeId: pti.toString() },
            replace: true,
          });
        }

        return next();
      } catch (error) {
        console.error('Configurator normalization failed:', error);
        return next();
      }
    },
  },

  {
    path: '/productTypes',
    name: 'productTypes',
    component: CustomizeOverview,
    props: (route) => ({
      productTypeId: route.query.productTypeId ? parseInt(route.query.productTypeId.toString(), 10) : 1,
      supplierUrl: route.query.supplier ?? 'aidddo',
    }),
  },

  {
    path: '/:supplier',
    redirect: (to) => {
      const supplier = to.params.supplier as string;
      return { path: '/', query: { ...to.query, supplier } };
    },
  },

  {
    path: '/projects',
    name: 'projects',
    component: ProjectPage,
    props: (route) => ({
      productTypeId: route.query.productTypeId ? parseInt(route.query.productTypeId.toString(), 10) : 13,
    }),
  },
  {
    path: '/templates',
    name: 'templates',
    component: TemplatePage,
    props: (route) => ({
      productTypeId: route.query.productTypeId ? parseInt(route.query.productTypeId.toString(), 10) : 13,
    }),
  },
  {
    path: '/template-workflow',
    name: 'template-workflow',
    component: TemplateWorkflow,
    props: (route) => ({
      templateId: route.query.templateId ? parseInt(route.query.templateId.toString(), 10) : null,
    }),
  },
  {
    path: '/template-upload',
    name: 'template-upload',
    component: TemplateUpload,
    props: (route) => ({
      templateId: route.query.templateId ? parseInt(route.query.templateId.toString(), 10) : null,
    }),
  },
  {
    path: '/orders',
    name: 'orders',
    component: OrderPage
  },
  {
    path: '/order-workflow',
    name: 'order-workflow',
    component: OrderWorkflow,
    props: (route) => ({
      templateId: route.query.templateId ? parseInt(route.query.templateId.toString(), 10) : null,
      orderId: route.query.orderId ? parseInt(route.query.orderId.toString(), 10) : null,
    }),
  },
  {
    path: '/order-upload',
    name: 'order-upload',
    component: OrderUpload,
    props: (route) => ({
      templateId: route.query.templateId ? parseInt(route.query.templateId.toString(), 10) : null,
      orderId: route.query.orderId ? parseInt(route.query.orderId.toString(), 10) : null,
    }),
  },

  { path: '/contact', name: 'contact', component: ContactPage },
  { path: '/login', component: LoginPage, meta: { layout: 'empty' } },
  { path: '/reset-password', component: ResetPassword },
  { path: '/register', component: RegisterPage },
  { path: '/forgot-password', component: ForgotPasswordPage },

  {
    path: '/admin',
    name: 'admin',
    component: AdminLayout,
    children: [
      { path: 'overview', name: 'admin-overview', component: AdminOverview },
      { path: 'domains', name: 'admin-domains', component: AdminDomainsPage },
      { path: 'domains/create', name: 'AdminDomainsCreate', component: AdminCreateDomainPage },
      { path: 'domains/:id/edit', name: 'AdminDomainsEdit', component: AdminEditDomainPage },
      { path: 'users', name: 'admin-users', component: AdminUsersPage },
      { path: '', redirect: { name: 'admin-overview' } },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const userStore = useUserStore();
  const isUserSupplier = userStore.roles.includes(Role.Supplier);
  const isUserAuthenticated = userStore.isAuthenticated;
  const supplierOnlyRoutes = ['projects', 'order-workflow', 'order-upload', 'templates', 'template-workflow', 'template-upload'];
  const authenticatedRoutes = ['orders'];

  if (!to.query.supplier) {
    const persisted = localStorage.getItem('supplier');
    if (persisted) {
      return next({ path: to.path, query: { ...to.query, supplier: persisted }, replace: true });
    }
  }

  if (supplierOnlyRoutes.includes(to.name as string) && !isUserSupplier) {
    return next({ name: 'configurator' });
  }

  if (authenticatedRoutes.includes(to.name as string) && !isUserAuthenticated) {
    return next({ name: 'configurator' });
  }

  if (to.name === 'template-workflow' && !to.query.id && from.name === 'template-upload' && from.query.id) {
    return next({ name: 'template-workflow', query: from.query });
  }

  if (to.name === 'order-workflow' && !to.query.orderId && from.name === 'order-upload' && from.query.orderId) {
    return next({ name: 'order-workflow', query: from.query });
  }

  return next();
});
router.afterEach((to) => {
  if (to.name === 'orders') {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('refresh-orders'));
    }, 0);
  }
});


export default router;
